import {useCallback, useEffect, useMemo} from 'react';
import {useFetchTournamentQuery} from '@goproblems/shared/api/tournaments';
import {useDispatch, useSelector} from './common';
import {uiSlice} from '@reducers';
import {Tournament} from '@types';

export const useTournament = (
    tournamentId?: number | string,
    options: {polling?: boolean} = {}
) => {
    const {user: loggedUser} = useSelector(state => state.auth);
    const {
        currentData: tournament,
        refetch,
        isLoading,
        isError,
    } = useFetchTournamentQuery(tournamentId, {
        skip: !tournamentId,
        skipPollingIfUnfocused: true,
        pollingInterval: options.polling ? 3000 : undefined,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });
    const dispatch = useDispatch();
    const setTournament = useCallback(
        (value: Tournament) => dispatch(uiSlice.actions.setTournament(value)),
        [dispatch]
    );
    useEffect(() => {
        if (tournament) setTournament(tournament);
    }, [tournament, setTournament]);

    const results = useMemo(
        () =>
            [...(tournament?.currentRound?.results ?? [])].sort(
                (a, b) =>
                    b.score - a.score ||
                    b.livesLeft - a.livesLeft ||
                    a.timeSpent - b.timeSpent
            ),
        [tournament?.currentRound?.results]
    );
    const players = useMemo(() => results.map(({player}) => player), [results]);
    const currentResult = results.find(
        ({player}) => player.user.id === loggedUser?.id
    );
    return {
        isLoading,
        isError,
        tournament,
        tournamentData: tournament,
        currentRound: tournament?.currentRound,
        players,
        playersCount: tournament?.playersCount ?? 0,
        joined: !!currentResult,
        results,
        currentPlayer: currentResult?.player,
        currentResult,
        rulesetConfiguration: tournament?.rulesetConfiguration,
        goThrough: tournament
            ? Math.floor(
                  Math.pow(
                      results.length,
                      tournament.rulesetConfiguration.exponent
                  )
              )
            : 0,
        setTournament,
        refetch,
    };
};
